import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {
  ResponsiveService,
  FrkBreakpoint,
  InfoItem,
} from '@frk/eds-components';
import { SubscriptionsService } from '@marketing/subscriptions-manage/services/subscriptions.service';
import {
  ApiParameters,
  DocumentsData,
  DocumentsResult,
  GridRowData,
  ProductSubscriptionsDTO,
  ProductsUserData,
  SubscribedProductDTO,
  SubscriptionProductSelectedFilters,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject, Observable } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { Logger } from '@utils/logger';
import { SubscriptionProductsService } from '@marketing/subscriptions-manage/services/subscription-products.service';

/**
 * Logger
 */
const logger = Logger.getLogger('SubscriptionsProductsIiiRowComponent');

@Component({
  selector: 'ft-subscriptions-products-iii-row',
  templateUrl: './subscriptions-products-iii-row.component.html',
  styleUrls: ['./subscriptions-products-iii-row.component.scss'],
})
export class SubscriptionsProductsIiiRowComponent implements OnInit, OnDestroy {
  public isError = false;
  public isLoading = false;
  public isSavingDocuments = false;
  public allDocumentsData: Array<DocumentsData> = [];
  public fundLiteratureDocuments: Array<DocumentsData> = [];
  public regulatoryDocuments: Array<DocumentsData> = [];
  public mobilePrimaryData: InfoItem[];
  public unSavedRowData: GridRowData;
  private documentData$: Observable<DocumentsResult>;
  private unsubscribe$: Subject<void> = new Subject<void>();
  public isCardView$: Observable<boolean>;
  public selectedDocuments: DocumentsData[] = [];
  private userData: ProductsUserData;
  private allActiveDocuments: ProductSubscriptionsDTO[] = [];
  private selectedFilters: SubscriptionProductSelectedFilters[] = [];
  public isAllDocumentsSelected = false;

  /**
   * data to render the table
   */
  @Input() rowData: GridRowData;

  @Input() apiUrl!: string;

  constructor(
    private responsiveService: ResponsiveService,
    private spinner: NgxSpinnerService,
    private changeDetector: ChangeDetectorRef,
    private subscriptionsService: SubscriptionsService,
    private subscriptionProductsService: SubscriptionProductsService
  ) {
    this.isCardView$ = this.responsiveService.isBreakpointOrBelow$(
      FrkBreakpoint.Tablet
    );
  }

  ngOnInit(): void {
    this.userData = this.subscriptionProductsService.getUserData();
    if (this.rowData?.allActiveDocuments?.length) {
      this.allActiveDocuments = this.rowData.allActiveDocuments;
    }
    this.mobilePrimaryData = [
      {
        label: 'Subscribed Documents',
        val: '',
      },
    ];
    this.subscriptionProductsService
      .getSingleRowData$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((rowData) => (this.unSavedRowData = rowData));
    this.subscriptionProductsService
      .getExpandedRowDocumentsData$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((documentsData) => (this.selectedDocuments = documentsData));
    this.subscriptionProductsService
      .getSelectedFilters$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((filters) => {
        this.selectedFilters = filters;
        if (this.rowData?.isExpanded) {
          this.setLoadingEnd();
          this.getDocumentData();
        }
      });
  }

  /**
   * Handle click event when we expand the accordion
   */
  public toggleExpand(): void {
    if (!this.unSavedRowData?.isExpanded) {
      this.setLoadingEnd();
      this.subscriptionProductsService.setSingleRowData$(this.rowData);
      this.rowData.isExpanded = true;
      this.getDocumentData();
    } else {
      if (this.selectedDocuments?.length === 0) {
        this.unSavedRowData.isExpanded = false;
        // this.unSavedRowData.isChecked = false;
        this.toggleExpand();
      } else {
        this.subscriptionProductsService.setIsProductsModalVisible$(true);
      }
    }
  }

  /**
   * fetching the document data on expanding of accordion
   */
  private getDocumentData(): void {
    const apiParameters: ApiParameters = {};
    this.selectedFilters.forEach((filter) => {
      switch (filter.filterType.toLowerCase()) {
        case 'share class':
          apiParameters.shareClass = filter.values
            .map((data) => data.value)
            .toString();
          break;
        case 'currency':
          apiParameters.shareClassCurrency = filter.values
            .map((data) => data.value)
            .toString();
          break;
        case 'document type':
          apiParameters.documentType = filter.values
            .map((data) => data.value)
            .toString();
          break;
        case 'hedged':
          apiParameters.shareClassHedged = filter.values
            .map((data) => data.value)
            .toString();
          break;
        case 'distribution':
          apiParameters.shareClassDistributionType = filter.values
            .map((data) => data.value)
            .toString();
          break;
        case 'language':
          apiParameters.language = filter.values
            .map((data) => data.value)
            .toString();
          break;
      }
    });
    this.isLoading = true;
    this.documentData$ = this.subscriptionsService.getDocumentTypeRequest$(
      this.apiUrl + `productDocuments/${this.rowData.PRODUCT_ID}`,
      apiParameters
    );
    this.documentData$.pipe(takeUntil(this.unsubscribe$)).subscribe(
      (documentTypeData: DocumentsResult) => {
        this.allDocumentsData = documentTypeData.documents;
        if (this.allDocumentsData?.length) {
          this.setTableData();
          // if (this.rowData?.isChecked) {
          //   this.checkUncheckAllDocuments(true);
          // } else {
          //   this.setTableData();
          // }
        } else {
          this.rowData.selectedDocumentsCount = 0;
        }
        this.isLoading = false;
        this.changeDetector.detectChanges();
      },
      (error) => {
        logger.debug('Document data load error', error);
        this.setLoadingEnd();
        this.isError = true;
      }
    );
  }

  /**
   * creating the table data for fund literature and regulatory documents
   */
  private setTableData() {
    this.spinner
      .show('documentSavingSpinner')
      .then(() => logger.debug('Document Saving Spinner set'));
    this.allDocumentsData.forEach((documentData: DocumentsData) => {
      const isDocumentSelected = this.rowData?.selectedDocuments?.some(
        (doc: DocumentsData) =>
          doc.widenAssetId === documentData.widenAssetId && doc.isChecked
      );
      const isAlreadySubscribed = this.userData.subscriptions.some(
        (doc) => doc.widenAssetId === documentData.widenAssetId && doc.active
      );
      if (isDocumentSelected || isAlreadySubscribed) {
        documentData.isChecked = true;
        this.allActiveDocuments.push(documentData);
      } else {
        documentData.isChecked = false;
      }
      if (documentData.category === 'Regulatory Documents') {
        this.regulatoryDocuments.push(documentData);
      } else {
        this.fundLiteratureDocuments.push(documentData);
      }
    });
    this.isAllDocumentsSelected =
      this.allDocumentsData?.length === this.allActiveDocuments?.length;

    this.rowData.allActiveDocuments = [...this.allActiveDocuments];
  }

  /**
   * Handle click event when we collapse the accordion
   */
  public toggleClose(): void {
    if (this.selectedDocuments?.length === 0) {
      this.rowData.isExpanded = false;
      // this.rowData.isChecked = false;
    } else {
      this.rowData.allActiveDocuments = [...this.allActiveDocuments];
      this.subscriptionProductsService.setIsProductsModalVisible$(true);
    }
    this.subscriptionProductsService.updateRowData(this.rowData);
  }

  // TODO - alex asked not to delete the implemention as
  // we may revert these changes in future. All the commented code will be revrted or deleted in future sprints
  /**
   * Handle click event when we click on row checkbox
   */
  // public onToggle(event: boolean): void {
  //   this.rowData.isChecked = event;
  //   if (!event) {
  //     this.setLoadingEnd();
  //     if (this.allDocumentsData?.length) {
  //       this.checkUncheckAllDocuments(event);
  //     }
  //   } else {
  //     if (
  //       !this.unSavedRowData?.isExpanded ||
  //       this.unSavedRowData?.PRODUCT_ID === this.rowData.PRODUCT_ID
  //     ) {
  //       this.setLoadingEnd();
  //       this.rowData.isChecked = event;
  //       this.rowData.isExpanded = event;
  //       this.subscriptionProductsService.setSingleRowData$(this.rowData);
  //       if (this.allDocumentsData?.length) {
  //         this.checkUncheckAllDocuments(event);
  //       } else {
  //         this.getDocumentData();
  //       }
  //     } else {
  //       this.checkUnsavedDocumentTypes();
  //     }
  //   }
  // }

  /**
   * to check the documents type for unsaved rows
   */
  // private checkUnsavedDocumentTypes() {
  //   if (this.unSavedRowData.selectedDocumentsCount === 0) {
  //     this.setLoadingEnd();
  //     this.unSavedRowData.isChecked = false;
  //     this.unSavedRowData.isExpanded = false;
  //     this.onToggle(true);
  //   } else {
  //     this.setLoadingEnd();
  //     this.rowData.isChecked = false;
  //     this.subscriptionProductsService.setIsProductsModalVisible$(true);
  //   }
  // }

  /**
   * to select all and to un-select all documents on click of toggle
   */
  public subscribeAllDocuments() {
    this.setLoadingEnd();
    this.allDocumentsData.forEach((documentData: DocumentsData) => {
      const isAlreadySubscribed = this.userData.subscriptions.some(
        (doc) => doc.widenAssetId === documentData.widenAssetId && doc.active
      );
      this.allActiveDocuments.push(documentData);
      if (!isAlreadySubscribed) {
        this.selectedDocuments.push(documentData);
      }
      documentData.isChecked = true;
      if (documentData.category === 'Regulatory Documents') {
        this.regulatoryDocuments.push(documentData);
      } else {
        this.fundLiteratureDocuments.push(documentData);
      }
    });
    this.subscriptionProductsService.setExpandedRowDocumentsData(
      this.selectedDocuments
    );
    this.rowData.allActiveDocuments = [...this.allActiveDocuments];
    this.rowData.selectedDocumentsCount = this.selectedDocuments?.length || 0;
    this.updateSubscription();
  }

  /**
   * on select/unselect of the checkbox in the accordion
   */
  public onSelectionChanged(event: boolean, data: DocumentsData): void {
    const docToUpdate: DocumentsData = this.selectedDocuments.find(
      (doc: DocumentsData) => doc.widenAssetId === data.widenAssetId
    );
    if (!event) {
      this.allActiveDocuments.splice(this.allActiveDocuments.indexOf(data), 1);
    } else {
      this.allActiveDocuments.push(data);
    }
    // this.rowData.isChecked =
    //   this.allDocumentsData?.length === this.allActiveDocuments?.length;

    if (docToUpdate) {
      this.selectedDocuments.splice(
        this.selectedDocuments.indexOf(docToUpdate),
        1
      );
    } else {
      data.isChecked = event;
      this.selectedDocuments.push(data);
    }
    this.subscriptionProductsService.setExpandedRowDocumentsData(
      this.selectedDocuments
    );
    this.rowData.selectedDocumentsCount = this.selectedDocuments?.length || 0;
  }

  /**
   * Update the subscription data
   */
  public updateSubscription() {
    const submitDocs: SubscribedProductDTO[] = this.subscriptionProductsService.getSubmittedDocumentsData(
      this.rowData,
      this.selectedDocuments
    );
    const url =
      this.apiUrl + 'productSubscriptions/globalId/' + this.userData.globalId;
    this.isSavingDocuments = true;
    this.subscriptionsService
      .getApiPutRequest$(url, submitDocs)
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => {
          return this.subscriptionsService.getApiGetRequest$(
            this.apiUrl +
              'productSubscriptions/globalId/' +
              this.userData.globalId
          );
        })
      )
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (userData: ProductsUserData) => {
          this.userData = userData;
          this.subscriptionProductsService.setUserData(userData);
          this.rowData.selectedDocuments = [...this.selectedDocuments];
          this.rowData.allActiveDocuments = [...this.allActiveDocuments];
          this.rowData.documentTypes = this.subscriptionProductsService.getActiveDocumentTypes(
            this.allActiveDocuments
          );
          this.rowData.isExpanded = false;
          // this.rowData.isChecked = false;
          this.subscriptionProductsService.setSingleRowData$(this.rowData);
          this.subscriptionProductsService.updateRowData(this.rowData);
          this.setLoadingEnd();
          logger.debug('Products saved', userData);
        },
        (error) => {
          logger.debug('Products save error', error);
        }
      );
  }

  /**
   * Set Loading Data End
   */
  private setLoadingEnd(): void {
    this.isLoading = false;
    this.isSavingDocuments = false;
    this.fundLiteratureDocuments = [];
    this.regulatoryDocuments = [];
    this.selectedDocuments = [];
    this.allActiveDocuments = [];
    this.changeDetector.detectChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
